import { decodeHTML } from "entities";

import { AuthenticatedUserModel } from "@courses-utils/gateway-auth";
import { parseCss } from "@use/parse-html";

import { htmlElements } from "@courses-pm2/api/src/ts/config";
import {
  Engine,
  isElement,
  isText,
  resolveMediaPath,
} from "@courses-pm2/api/src/ts/utils";

import {
  ElementLinkModel,
  GlossaryWordModel,
  GridModel,
  InternalLinkModel,
  InternalLinkTargets,
  RichtextContentNode,
  RichtextHtmlNode,
  RichtextHtmlNodeProps,
  RichtextModel,
} from "@courses-pm2/api/src/ts/models";

import { getChildrenByTagName } from "@courses-pm2/api/src/ts/parser/getChildrenByTagName";
import { parseCaptionedContentElement } from "@courses-pm2/api/src/ts/parser/parseCaptionedContentElement";
import { parseContentElement } from "@courses-pm2/api/src/ts/parser/parseContentElement";
import { parseElement } from "@courses-pm2/api/src/ts/parser/parseElement";

/**
 * A regular expression that can be used to check if the tag name of a given
 * node is representing a block element.
 */
// tslint:disable-next-line max-line-length
const blockTagRegEx =
  /^(div|p|h1|h2|h3|h4|h5|h6|Accordion|Audio|ComplexContent|Component|Ibook|Ibook3|Ibook4|Image|Iframe|Taskenginelink|Placeholder|RichText|Rows|SimpleText|Slider|Video)$/i;

/**
 * Specification of core arguments given to parser methods.
 */
type ParserArgs = {
  node: Element;
  portalId: string;
  user?: AuthenticatedUserModel;
  engine?: Engine;
};

/**
 * Helper that'll safely parse some simple html tags, which Gyldendal's editors
 * have been allowed to enter in certain parts of the application.
 */
export async function parseRichtext({
  nested,
  node,
  portalId,
  user,
  engine,
  solid,
  textNodeName,
}: ParserArgs & {
  nested?: boolean;
  solid?: boolean;
  textNodeName?: string;
}): Promise<RichtextModel | undefined> {
  // Extract reference to the value node
  const contentNode = getChildrenByTagName(node, textNodeName ?? "Value")[0];

  // Bail out if unable to extract value node
  if (!contentNode) {
    return undefined;
  }

  // Announce that a model has been identified if possible
  if (engine) {
    engine.announce(RichtextModel);
  }

  return new RichtextModel({
    engine,
    portalId,

    // Extract basic configurations of the component
    autoCenter: node.getAttribute("centerText") === "true",
    collapsible: node.getAttribute("isCollapssible") === "true",
    nested,
    solid,

    // Propagate extracted content
    content: await recursivelyParseChildren({
      node: contentNode,
      portalId,
      user,
      engine,
    }),

    // Propagate basic content element configurations
    ...parseCaptionedContentElement(node),
  });
}

/**
 * Helper htat'll iterate over the children of the given node, and recursively
 * convert the content to an easily renderable data structure.
 */
async function recursivelyParseChildren({
  node,
  portalId,
  user,
  engine,
  ignoreText,
}: ParserArgs & {
  ignoreText?: boolean;
}): Promise<RichtextContentNode[]> {
  // Iterate over the children and begin parsing them
  const children = Array.from(node.childNodes);
  const promises: Promise<RichtextContentNode | RichtextContentNode[]>[] =
    children.map(async (child) => {
      return parseChild({ node: child, portalId, user, engine, ignoreText });
    });

  // Flatten returned items into a one-dimensional array
  const content: RichtextContentNode[] = [];

  (await Promise.all(promises)).forEach((value) => {
    if (Array.isArray(value)) {
      for (const item of value) {
        content.push(item);
      }
    } else {
      content.push(value);
    }
  });

  return content;
}

/**
 * Helper that parses a single child node.
 */
async function parseChild({
  node,
  portalId,
  user,
  engine,
  ignoreText,
}: {
  node: ChildNode;
  portalId: string;
  user?: AuthenticatedUserModel;
  engine?: Engine;
  ignoreText?: boolean;
}): Promise<RichtextContentNode | RichtextContentNode[]> {
  if (isText(node) && ignoreText !== true) {
    return parseText(node);
  }

  if (isElement(node)) {
    const tagName = node.tagName.toLowerCase();

    // First of all, try parsing root elements
    const element = await parseElement({ node, portalId, user, engine });

    if (element !== undefined) {
      return element;
    }

    // If this is a built-in html element, then we handle it internally
    if (htmlElements[<keyof typeof htmlElements>tagName] === true) {
      return parseHtmlElement({ node, portalId, user, engine });
    }

    // ... Otherwise fallback try to handle custom elements that's
    // specific to Richtext elements or fallback to parsing of root
    // elements
    switch (tagName) {
      case "document":
        return parseDocumentLink({ node, portalId, user, engine });

      case "downloadlink":
        return parseDownloadLink({ node, portalId, user, engine });

      case "emptyimage":
        return {
          tagName: "div",
          props: {
            className: "emptyImage",
          },
          children: [],
        };

      case "elementlink":
        return parseElementLink({ node, portalId, user, engine });

      case "externallink":
        return parseExternalLink({ node, portalId, user, engine });

      case "glossaryword":
        return parseGlossaryWord({ node, portalId, user, engine });

      case "internallink":
        return parseInternalLink({ node, portalId, user, engine });

      default:
        // If we get here, there's no support for the given tag, so
        // simply bail out
        return undefined;
    }
  }

  // Ignore any other kind of nodes...
  return undefined;
}

/**
 * Helper that'll parse out the content of a DOM text node, automatically
 * wrapping it in a <span />-tag, if the node contains anything else than
 * whitespace.
 */
function parseText(node: Text): RichtextHtmlNode | string {
  const textContent = decodeHTML(node.nodeValue || "");

  return textContent.trim()
    ? {
        tagName: "span",
        props: {},
        children: textContent,
      }
    : textContent;
}

/**
 * Helper that'll parse html elements (in a lot of cases, we're going to need
 * to apply some custom parsing).
 */
async function parseHtmlElement(
  args: ParserArgs,
): Promise<RichtextContentNode[] | RichtextContentNode> {
  switch (args.node.tagName.toLowerCase()) {
    case "div":
      return parseDivElement(args);

    case "span":
      return parseSpanElement(args);

    case "a":
      return parseAnchorElement(args);

    case "table":
      const bordered = args.node.classList.contains("table-bordered");

      return {
        tagName: "div",
        props: {
          className: `richtext__table ${
            bordered ? "richtext__table--bordered" : ""
          }`,
        },
        children: await parseRawHtmlElement({
          ...args,
          ignoreText: true,
          classNames: [
            "table-alternative",
            "table-bordered",
            "table-hover",
            "table-striped",
          ],
        }),
      };

    case "thead":
    case "tbody":
    case "tfoot":
      return parseRawHtmlElement({
        ...args,
        ignoreText: true,
      });

    case "tr":
      return parseRawHtmlElement({
        ...args,
        ignoreText: true,
        styles: ["height"],
      });

    case "td":
    case "th":
      return parseTableCell(args);

    case "ol":
    case "ul":
      return parseListElement(args);

    default:
      return parseRawHtmlElement(args);
  }
}

/**
 * Helper that'll determine if a <div />-tag should be interpreted as a nested
 * RichtextModel or a GridModel - otherwise falling back to raw html parsing.
 */
// tslint:disable-next-line cyclomatic-complexity
async function parseDivElement({
  node,
  portalId,
  user,
  engine,
}: ParserArgs): Promise<RichtextContentNode[] | RichtextContentNode> {
  // Nodes with a className of "row" will need to be parsed as a grid, so that
  // we can properly align the embedded content
  if (node.className === "row") {
    return parseMediaGridElement({ node, portalId, user, engine });
  }

  // Nodes with an element-type applied means that they should be rendered as
  // nested Richtext elements
  if (node.className.indexOf("element-type") === 0) {
    return parseNestedRichtextElement({ node, portalId, user, engine });
  }

  // If the div-tag doesn't have any attributes of it's own, then determine if
  // it's actually just a filler?
  if (node.getAttributeNames().length === 0) {
    // Extract a list of child nodes that are contained within the div-tag,
    // excluding all text nodes that consists only of whitespace
    const contentNodes: ChildNode[] = [];

    for (const child of Array.from(node.childNodes)) {
      // Accept only text nodes that consists of something other than
      // whitespace
      if (isText(child)) {
        if (child.nodeValue && child.nodeValue.trim()) {
          contentNodes.push(child);
        }
      }

      // Accept all element nodes
      if (isElement(child)) {
        contentNodes.push(child);
      }
    }

    const firstChild = contentNodes[0];
    let lastChild = contentNodes[contentNodes.length - 1] || firstChild;

    // If this tag ONLY includes a linebreak, then unwrap it
    if (
      firstChild &&
      firstChild === lastChild &&
      isElement(firstChild) &&
      firstChild.tagName.toLowerCase() === "br"
    ) {
      return {
        tagName: "br",
        props: {},
        children: undefined,
      };
    }

    // If the last child is a <br />-tag, then we can ignore it.. It has
    // zero effect on the flow of the content as the parent <div /> will
    // already break the flow!
    if (
      lastChild &&
      isElement(lastChild) &&
      lastChild.tagName.toLowerCase() === "br"
    ) {
      lastChild = contentNodes[contentNodes.length - 2] || firstChild;
    }

    // If this tag starts and ends with a block tag (div / p / heading),
    // then unwrap it's content
    if (
      firstChild &&
      isElement(firstChild) &&
      lastChild &&
      isElement(lastChild) &&
      firstChild.tagName.match(blockTagRegEx) &&
      lastChild.tagName.match(blockTagRegEx)
    ) {
      return firstChild !== lastChild
        ? recursivelyParseChildren({ node, portalId, user, engine })
        : parseChild({ node: firstChild, portalId, user, engine });
    }
  }

  // If we get here, this is just a plain ol' <div> element, which allows
  // using white-space inline styling
  return parseRawHtmlElement({
    node,
    portalId,
    user,
    engine,
    styles: ["whiteSpace"],
  });
}

/**
 * Helper that'll generate a GridModel for nested <div class="row"> elements, so
 * we can use the built-in mechanism for grids.
 */
async function parseMediaGridElement({
  node,
  portalId,
  user,
  engine,
}: ParserArgs): Promise<GridModel | RichtextHtmlNode> {
  // Announce that a model has been identified if possible
  if (engine) {
    engine.announce(GridModel);
  }

  const wrapper = node.querySelector(".complex-column");

  // Bail out if unable to find the wrapper node, as we will not be able to
  // reliably extract content in that case
  if (!wrapper) {
    if (process.env.NODE_ENV !== "production") {
      console.warn(
        `parseMediaGridElement(...): Unable to extract .complex-column wrapper`,
      );
    }

    return parseRawHtmlElement({ node, portalId, user, engine });
  }

  // ... Otherwise find the first and last container elements within the grid
  const children = getChildrenByTagName(wrapper, "div");
  const container1 = children[0];
  const container2 = children[children.length - 1];

  // Bail out if we're unable to extract two distinct containers from within
  // the grid
  if (!container1 || !container2 || container1 === container2) {
    if (process.env.NODE_ENV !== "production") {
      console.warn(
        `parseMediaGridElement(...): Unable to extract columns in .complex-column wrapper`,
      );
    }

    return parseRawHtmlElement({ node, portalId, user, engine });
  }

  // Once we get here we'll need to determine, if we should reverse the order
  // of the columns
  const reverseOrder =
    container1.classList.contains("wrap-right") ||
    container2.classList.contains("wrap-left");

  // Next up we'll need to determine the actual wrapper of the content within
  // each column (if wrap-left / wrap-right has been applied, that means the
  // column will contain a nested <div>-tag, which we're not really interested
  // in outputting to the browser DOM)
  const column1 =
    container1.classList.contains("wrap-left") ||
    container1.classList.contains("wrap-right")
      ? getChildrenByTagName(container1, "div")[0] || container1
      : container1;

  const column2 =
    container2.classList.contains("wrap-left") ||
    container2.classList.contains("wrap-right")
      ? getChildrenByTagName(container2, "div")[0] || container2
      : container2;

  // Bail out if we're unable to extract contents for each of the columns
  if (!column1 || !column2) {
    if (process.env.NODE_ENV !== "production") {
      console.warn(
        `parseMediaGridElement(...): Unable to extract column content`,
      );
    }

    return parseRawHtmlElement({ node, portalId, user, engine });
  }

  // If we get here, we're ready to create the GridModel with the nested
  // content!
  let columnContent = await Promise.all([
    recursivelyParseChildren({ node: column1, portalId, user, engine }),
    recursivelyParseChildren({ node: column2, portalId, user, engine }),
  ]);

  // Reverse order of the column if that what we're supposed to do
  if (reverseOrder) {
    // @ts-ignore TypeScript infers columnContent to be a tuple, which it
    // doesn't like using .reverse() on
    columnContent = columnContent.reverse();
  }

  // Once we get here, we're ready to create a GridModel based on the media
  // and text content
  return new GridModel({
    isScrambled: false,
    engine,
    portalId,
    columns: [
      {
        size: 4,
        content: columnContent[0],
      },
      {
        size: 4,
        content: columnContent[1],
      },
    ],
  });
}

/**
 * Helper that'll generate a RichtextModel for nested richtext elements, so that
 * we can properly handle them within the clients.
 */
async function parseNestedRichtextElement({
  node,
  portalId,
  user,
  engine,
}: ParserArgs): Promise<RichtextModel> {
  // Extract theme from class name
  const themeRegExMatch = node.className.match(/ icon-([^\ ]+)/);
  const theme =
    themeRegExMatch && themeRegExMatch[1] ? themeRegExMatch[1] : undefined;

  return new RichtextModel({
    engine,
    portalId,

    // Apply basic configuration
    autoCenter: false,
    collapsible: false,
    nested: true,
    solid: false,

    // Propagate extracted content
    content: await recursivelyParseChildren({ node, portalId, user, engine }),

    // Propagate basic content element configurations
    ...parseContentElement(node),

    // Propagate custom theme configurations
    theme,
    ribbon: node.getAttribute("iconLetter") || undefined,
  });
}

/**
 * Helper that'll determine if a <span />-tag should be interpreted as a nested
 * GlossaryWordModel - otherwise falling back to raw html parsing.
 */
async function parseSpanElement({
  node,
  portalId,
  user,
  engine,
}: ParserArgs): Promise<RichtextHtmlNode | GlossaryWordModel | undefined> {
  // Ignore element if it's empty!
  if (!node.textContent || !node.textContent.trim()) {
    return undefined;
  }

  if (node.className === "ordforklaring") {
    // Announce that a model has been identified if possible
    if (engine) {
      engine.announce(GlossaryWordModel);
    }

    return new GlossaryWordModel({
      isScrambled: false,
      engine,
      portalId,
      content: await recursivelyParseChildren({ node, portalId, user, engine }),
      id: node.getAttribute("data-opslag") || "",
    });
  }

  // If we get here, this is just a plain ol' <span> element, which allows
  // using white-space inline styling
  return parseRawHtmlElement({
    node,
    portalId,
    user,
    engine,
    styles: ["whiteSpace"],
  });
}

/**
 * Helper that'll transform an anchor element into either an InternalLinkModel
 * or simply return a html anchor node.
 */
async function parseAnchorElement({
  node,
  portalId,
  user,
  engine,
}: ParserArgs): Promise<InternalLinkModel | RichtextHtmlNode> {
  // Determine if this is an internal link pointing to a page within the
  // current portal
  if (
    (node.getAttribute("data-element-type") || "").toLowerCase() ===
    "internallink"
  ) {
    // Announce that a model has been identified if possible
    if (engine) {
      engine.announce(InternalLinkModel);
    }

    // Determine the desired target of the link
    let target: InternalLinkTargets = InternalLinkTargets.SAME_WINDOW;

    if (node.getAttribute("target") === "_blank") {
      target = InternalLinkTargets.NEW_WINDOW;
    }

    if (node.getAttribute("lightbox") === "true") {
      target = InternalLinkTargets.LIGHTBOX;
    }

    // Return the internal link model
    return new InternalLinkModel({
      isScrambled: false,
      engine,
      portalId,
      href: node.getAttribute("href") || "",
      target,
      content: await recursivelyParseChildren({ node, portalId, user, engine }),
    });
  }

  // ... Otherwise handle the link as a plain ol' anchor tag pointing to an
  // external page
  return {
    tagName: "a",
    props: {
      href: node.getAttribute("href") || "",
      target: "_blank",
      className: "externalLink",
    },
    children: await recursivelyParseChildren({ node, portalId, user, engine }),
  };
}

/**
 * Helper that'll parse <td>- and <th>-tags, which contain multiple possible
 * configurations on both inline-styling and attributes that needs to be
 * merged properly.
 */
async function parseTableCell({
  node,
  portalId,
  user,
  engine,
}: ParserArgs): Promise<RichtextHtmlNode> {
  // Prepare the props we're going to propagate to the node
  const props: {
    className?: string;
    width?: string;
    colSpan?: number;
    rowSpan?: number;
    style: {
      [key: string]: string;
    };
  } = {
    style: {},
  };

  // If the node has a valign property added to it, then we'll need to convert
  // that to it's corresponding vertical-align inline styling
  const valign = (node.getAttribute("valign") || "").toLowerCase();

  switch (valign) {
    case "top":
    case "middle":
    case "bottom":
      props.style.verticalAlign = valign;
      break;

    default:
    // No other values are known, ignore...
  }

  // If a node has an align property added to it, then we'll need to convert
  // that to it's corresponding text-align inline styling
  const align = (node.getAttribute("align") || "").toLowerCase();

  switch (align) {
    case "left":
    case "center":
    case "right":
      props.style.textAlign = align;
      break;

    default:
    // No other values are known, ignore...
  }

  // Extract allowed custom style properties
  const css = parseCss(node.getAttribute("style") || "");

  if (css) {
    props.style.width = css.width;
    props.style.verticalAlign = css.verticalAlign || props.style.verticalAlign;
    props.style.textAlign = css.textAlign || props.style.textAlign;
  }

  // Propagate "width" attribute directly as a prop
  props.width = node.getAttribute("width") || undefined;

  // Propagate colSpan and rowSpan props
  props.colSpan = parseInt(node.getAttribute("colspan") || "1", 10);
  props.rowSpan = parseInt(node.getAttribute("rowspan") || "1", 10);

  if (props.colSpan === 1) {
    props.colSpan = undefined;
  }

  if (props.rowSpan === 1) {
    props.rowSpan = undefined;
  }

  // Add multipleColumns and/or multipleRows class names to allow custom
  // styling of cells that span multiple rows / columns
  const appliedClassNames: string[] = [];

  if (props.colSpan) {
    appliedClassNames.push("multipleColumns");
  }

  if (props.rowSpan) {
    appliedClassNames.push("multipleRows");
  }

  props.className = appliedClassNames.join(" ");

  // Finally return an HTML node representing this piece of content
  return {
    tagName: node.tagName,
    props,
    children: await recursivelyParseChildren({ node, portalId, user, engine }),
  };
}

/**
 * Helper that'll parse <ol>- and <ul>-tags, which contain multiple possible
 * configurations on both inline-styling and attributes that needs to be
 * merged properly.
 */
async function parseListElement({
  node,
  portalId,
  user,
  engine,
}: ParserArgs): Promise<RichtextHtmlNode> {
  // Prepare the props we're going to propagate to the node
  const props: {
    start?: string;
    type?: string; // tslint:disable-line no-reserved-keywords
    style: {
      [key: string]: string;
    };
  } = {
    style: {},
  };

  // Extract plain props from the node
  props.start = node.getAttribute("start") || undefined;
  props.type = node.getAttribute("type") || undefined;

  // There's a few legacy class names from the old pm2 frontend, which we'll
  // need to manually transform to their corresponding inline styled values
  if (node.classList.contains("list-style-upper-latin")) {
    props.style.listStyleType = "upper-latin";
  }

  if (node.classList.contains("list-style-lower-latin")) {
    props.style.listStyleType = "lower-latin";
  }

  // But we also allow overwriting the list-style directly in the given inline
  // css
  const css = parseCss(node.getAttribute("style") || "");

  if (css) {
    // If list-style is given, then attempt to extract the list-style-type
    // from it
    // tslint:disable-next-line max-line-length
    const listStyleRegExMatch = (css.listStyle || "").match(
      /disc|armenian|circle|cjk-ideographic|decimal|decimal-leading-zero|georgian|hebrew|hiragana|hiragana-iroha|katakana|katakana-iroha|lower-alpha|lower-greek|lower-latin|lower-roman|none|square|upper-alpha|upper-greek|upper-latin|upper-roman/i,
    );

    if (listStyleRegExMatch) {
      props.style.listStyleType = listStyleRegExMatch[0];
    }

    // We're always going to allow the list-style-type configuration
    props.style.listStyleType = css.listStyleType || props.style.listStyleType;
  }

  // Finally return an HTML node representing this piece of content
  return {
    tagName: node.tagName,
    props,
    children: await recursivelyParseChildren({ node, portalId, user, engine }),
  };
}

/**
 * Helper that'll parse raw html elements (ie. elements which we will not allow
 * any kind of attributes).
 */
async function parseRawHtmlElement({
  node,
  portalId,
  user,
  engine,
  ignoreText,
  classNames,
  styles,
}: ParserArgs & {
  ignoreText?: boolean;
  classNames?: string[];
  styles?: string[];
}): Promise<RichtextHtmlNode> {
  const props: RichtextHtmlNodeProps = {};

  // If any styles are allowed, then extract them and push to the props that
  // we're propagating to the node
  if (styles) {
    // First of all, parse the inline css into an object which we can use
    const css = parseCss(node.getAttribute("style") || "");

    if (css) {
      // Next up, pick only the allowed styles
      props.style = {};

      for (const style of styles) {
        props.style[style] = css[style];
      }
    }
  }

  // If any classes are allowed, then extract them and push to the props that
  // we're propagating to the node
  if (classNames) {
    const appliedClassNames: string[] = [];

    for (const className of classNames) {
      appliedClassNames.push(className);
    }

    props.className = appliedClassNames.join(" ");
  }

  return {
    tagName: node.tagName,
    props,
    children: await recursivelyParseChildren({
      node,
      portalId,
      user,
      engine,
      ignoreText,
    }),
  };
}

/**
 * Helper that'll parse <GlossaryWord /> custom elements nested within the
 * Richtext feed.
 */
async function parseGlossaryWord({
  node,
  portalId,
  user,
  engine,
}: ParserArgs): Promise<GlossaryWordModel | undefined> {
  // Ignore element if it's empty!
  if (!node.textContent || !node.textContent.trim()) {
    return undefined;
  }

  // Announce that a model has been identified if possible
  if (engine) {
    engine.announce(GlossaryWordModel);
  }

  return new GlossaryWordModel({
    isScrambled: false,
    engine,
    portalId,
    id: node.getAttribute("id") || "",
    content: await recursivelyParseChildren({ node, portalId, user, engine }),
  });
}

/**
 * Helper that'll parse <InternalLink /> custom elements nested within the
 * Richtext feed.
 */
async function parseInternalLink({
  node,
  portalId,
  user,
  engine,
}: ParserArgs): Promise<InternalLinkModel | undefined> {
  // Ignore element if it's empty!
  if (!node.textContent || !node.textContent.trim()) {
    return undefined;
  }

  // Announce that a model has been identified if possible
  if (engine) {
    engine.announce(InternalLinkModel);
  }

  // Determine the desired target of the link
  let target: InternalLinkTargets = InternalLinkTargets.SAME_WINDOW;

  if (node.getAttribute("target") === "_blank") {
    target = InternalLinkTargets.NEW_WINDOW;
  }

  if (node.getAttribute("lightbox") === "true") {
    target = InternalLinkTargets.LIGHTBOX;
  }

  // Return the internal link model
  return new InternalLinkModel({
    isScrambled: false,
    engine,
    portalId,
    href: node.getAttribute("url") || "",
    target,
    content: await recursivelyParseChildren({ node, portalId, user, engine }),
  });
}

/**
 * Helper that'll parse <ElementLink /> custom elements nested within the
 * Richtext feed.
 */
async function parseElementLink({
  node,
  portalId,
  user,
  engine,
}: ParserArgs): Promise<ElementLinkModel | undefined> {
  // Ignore element if it's empty!
  if (!node.textContent || !node.textContent.trim()) {
    return undefined;
  }

  // Announce that a model has been identified if possible
  if (engine) {
    engine.announce(ElementLinkModel);
  }

  // Determine the desired target of the link
  let target: InternalLinkTargets = InternalLinkTargets.SAME_WINDOW;

  if (node.getAttribute("target") === "_blank") {
    target = InternalLinkTargets.NEW_WINDOW;
  }

  if (node.getAttribute("lightbox") === "true") {
    target = InternalLinkTargets.LIGHTBOX;
  }

  // Return the internal link model
  return new ElementLinkModel({
    isScrambled: false,
    engine,
    portalId,
    elementId: node.getAttribute("id") || "",
    target,
    content: await recursivelyParseChildren({ node, portalId, user, engine }),
  });
}

/**
 * Helper that'll parse <ExternalLink /> custom elements nested within the
 * Richtext feed.
 */
async function parseExternalLink({
  node,
  portalId,
  user,
  engine,
}: ParserArgs): Promise<RichtextHtmlNode | undefined> {
  // Ignore element if it's empty!
  if (!node.textContent || !node.textContent.trim()) {
    return undefined;
  }

  return {
    tagName: "a",
    props: {
      href: node.getAttribute("url") || "",
      target: "_blank",
      className: "externalLink",
    },
    children: await recursivelyParseChildren({ node, portalId, user, engine }),
  };
}

/**
 * Helper that'll parse <Document /> custom elements nested within the
 * Richtext feed.
 */
async function parseDocumentLink({
  node,
  portalId,
  user,
  engine,
}: ParserArgs): Promise<RichtextHtmlNode | undefined> {
  // Ignore element if it's empty!
  if (!node.textContent || !node.textContent.trim()) {
    return undefined;
  }

  return {
    tagName: "a",
    props: {
      href: node.getAttribute("file") || "",
      target: "_blank",
      className: "documentLink",
    },
    children: await recursivelyParseChildren({ node, portalId, user, engine }),
  };
}

/**
 * Helper that'll parse <DownloadLink /> custom elements nested within the
 * Richtext feed.
 */
async function parseDownloadLink({
  node,
  portalId,
  user,
  engine,
}: ParserArgs): Promise<RichtextHtmlNode | undefined> {
  // Ignore element if it's empty!
  if (!node.textContent || !node.textContent.trim()) {
    return undefined;
  }

  return {
    tagName: "a",
    props: {
      href: resolveMediaPath(node.getAttribute("url")),
      target: "_blank",
      className: "downloadLink",
    },
    children: await recursivelyParseChildren({ node, portalId, user, engine }),
  };
}
